import React, { useCallback, useMemo, useRef, useState } from 'react';

import { AutocompleteCDS } from 'app/shared/componentsRestyled/coreDesignSystem/form';
import { WithLoadingPropTypes } from 'hocs/withLoading';
import _ from 'lodash';
import PropTypes from 'prop-types';

import useDebounce from 'hooks/useDebounce';
import { useEffectSafeStrictMode } from 'hooks/useEffectSafeStrictMode';

const DEBOUNCING_TIME = 1000;
const DEFAULT_PAGE_SIZE = 10;

const AutocompleteWithInfinityLoad = ({
  setIsLoading = _.noop,
  onLoadMore = _.noop,
  pageSize = DEFAULT_PAGE_SIZE,
  dataTestId,
  ...restProps
}) => {
  const inputRef = useRef(null);
  const optionsPageRef = useRef(1);
  const [options, setOptions] = useState([]);

  const onLoad = useCallback(
    ({ filterText, pageNumber, pageSize }) => {
      setIsLoading(true);

      return onLoadMore({ filterText, pageNumber, pageSize })
        .then(options => {
          const isFirstFetch = pageNumber === 1;

          if (isFirstFetch) {
            setOptions(options);

            return;
          }

          setOptions(prevOptions => [...prevOptions, ...options]);
        })
        .finally(() => setIsLoading(false));
    },
    [onLoadMore, setIsLoading],
  );

  const onLoadMoreDebounced = useDebounce(async filterText => {
    await onLoad({ filterText, pageNumber: optionsPageRef.current, pageSize });
    inputRef?.current?.focus();
  }, DEBOUNCING_TIME);

  const listBoxProps = useMemo(
    () => ({
      ...restProps.ListboxProps,
      onScroll: e => {
        const { scrollTop, clientHeight, scrollHeight } = e.currentTarget;
        const isNeedLoad = Math.round(scrollTop + clientHeight) === scrollHeight;

        if (isNeedLoad) {
          optionsPageRef.current += 1;
          onLoad({ filterText: '', pageNumber: optionsPageRef.current, pageSize });
        }
      },
    }),
    [restProps.ListboxProps, pageSize, onLoad],
  );

  const inputProps = useMemo(() => ({ inputRef }), []);

  const onInputChange = useCallback(
    (...args) => {
      if (onLoadMoreDebounced) {
        const value = args[1];
        const filterText = value.trim();
        optionsPageRef.current = 1;

        onLoadMoreDebounced(filterText);
      }

      restProps.onInputChange && restProps.onInputChange(...args);
    },
    [restProps, onLoadMoreDebounced],
  );

  useEffectSafeStrictMode(() => {
    onLoad({ filterText: '', pageNumber: optionsPageRef.current, pageSize });
  }, [onLoad, pageSize]);

  return (
    <AutocompleteCDS
      isLazyWrapped
      ListboxProps={listBoxProps}
      dataTestId={dataTestId}
      inputProps={inputProps}
      options={options}
      onInputChange={onInputChange}
      {...restProps}
    />
  );
};

AutocompleteWithInfinityLoad.propTypes = {
  dataTestId: PropTypes.string,
  pageSize: PropTypes.number,
  setIsLoading: WithLoadingPropTypes.setIsLoading,
  onLoadMore: PropTypes.func,
};

export default AutocompleteWithInfinityLoad;
